/**
 * AgentAvatar.jsx — Prospect photo avatar with initials fallback
 *
 * Loads from /api/agent-photos/First_Last.jpg
 * Falls back to colored circle with initials on 404 or error.
 */
import { useState } from 'react';
import { Avatar } from '@mui/material';

/** Strip characters that get removed in photo filenames */
function nameToFilename(name) {
  if (!name) return '';
  return name.replace(/['\.\-]/g, '').replace(/ /g, '_') + '.jpg';
}

/** Deterministic color from a string */
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E',
    '#14B8A6', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
    '#A855F7', '#EC4899',
  ];
  return colors[Math.abs(hash) % colors.length];
}

/** Get initials from a name (first + last) */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function AgentAvatar({ name, size = 32, sx = {} }) {
  const [imgError, setImgError] = useState(false);
  const filename = nameToFilename(name);
  const src = filename ? `/api/agent-photos/${encodeURIComponent(filename)}` : '';
  const initials = getInitials(name);
  const color = stringToColor(name || '');

  if (!filename || imgError) {
    return (
      <Avatar
        sx={{
          width: size, height: size, fontSize: size * 0.4,
          bgcolor: color, color: '#fff', fontWeight: 700,
          ...sx,
        }}
      >
        {initials}
      </Avatar>
    );
  }

  return (
    <Avatar
      src={src}
      alt={name}
      onError={() => setImgError(true)}
      sx={{ width: size, height: size, ...sx }}
    >
      {initials}
    </Avatar>
  );
}
